import { Camera, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'

/**
 * Receipt-photo capture.
 *
 * Drivers shoot the supervisor's signed paper receipt with the
 * truck's mobile camera. We use the standard
 * `<input type="file" accept="image/*" capture="environment">`
 * pattern: it's the only API that triggers the OS-native camera
 * UI on iOS and Android without asking for `getUserMedia`
 * permissions or building our own viewfinder.
 *
 * Notes:
 *   - `capture="environment"` requests the rear camera. Older
 *     browsers ignore the attribute and fall back to a file
 *     picker, which is also fine.
 *   - We refuse files larger than 5 MiB on the client *before*
 *     uploading. The API enforces the same limit, but failing
 *     fast saves a 4G round-trip on a phone with poor reception.
 *   - The preview comes from `URL.createObjectURL`, which we
 *     dutifully revoke on unmount / replacement to prevent the
 *     blob from being kept alive in memory.
 *   - Parent owns the `File`; we just emit it.
 */

const MAX_BYTES = 5 * 1024 * 1024
const ALLOWED_MIME = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']

interface PhotoCaptureProps {
  label: string
  onPhotoChange: (file: File | null) => void
  /** External error (e.g. server-side rejection). */
  error?: string | null
}

export function PhotoCapture({ label, onPhotoChange, error }: PhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  // Manage the object URL lifecycle so the GC can reclaim the
  // blob once the user replaces or removes the photo.
  useEffect(() => {
    if (file === null) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  function handleFiles(picked: FileList | null): void {
    setLocalError(null)
    if (picked === null || picked.length === 0) return
    const next = picked[0]
    if (next === undefined) return

    if (next.size > MAX_BYTES) {
      setLocalError(
        `La photo dépasse 5 Mo (${(next.size / 1024 / 1024).toFixed(1)} Mo).`,
      )
      onPhotoChange(null)
      setFile(null)
      return
    }
    if (next.type !== '' && !ALLOWED_MIME.includes(next.type)) {
      setLocalError('Format non supporté (PNG, JPEG ou WEBP attendu).')
      onPhotoChange(null)
      setFile(null)
      return
    }
    setFile(next)
    onPhotoChange(next)
  }

  function clear(): void {
    setFile(null)
    setLocalError(null)
    onPhotoChange(null)
    if (inputRef.current !== null) {
      // Reset the input so picking the *same* file again still
      // fires `change`.
      inputRef.current.value = ''
    }
  }

  const displayedError = error ?? localError

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-text">{label}</span>

      {/* The actual file input is invisible — the styled button
          below proxies clicks to it. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {file === null ? (
        <Button
          type="button"
          variant="secondary"
          size="lg"
          fullWidth
          onClick={() => inputRef.current?.click()}
        >
          <Camera size={20} />
          Prendre une photo
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <div
            className="relative rounded-xl overflow-hidden border-2 border-surface-border bg-surface"
            // Constrain preview height so a 4032×3024 phone photo
            // doesn't dominate the page.
            style={{ maxHeight: '320px' }}
          >
            {preview !== null ? (
              <img
                src={preview}
                alt="Aperçu du bon"
                className="w-full h-auto max-h-[320px] object-contain bg-surface-muted"
              />
            ) : null}
          </div>
          <div className="flex items-center justify-between text-xs text-text-muted">
            <span className="truncate">
              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} Mo)
            </span>
            <button
              type="button"
              onClick={clear}
              className={[
                'inline-flex items-center gap-1 px-2 min-h-[2.5rem] rounded-lg',
                'text-danger hover:bg-danger-soft active:bg-danger-soft',
                'transition-colors duration-100',
              ].join(' ')}
              aria-label="Supprimer la photo"
            >
              <Trash2 size={16} aria-hidden="true" />
              Supprimer
            </button>
          </div>
        </div>
      )}

      {displayedError !== undefined && displayedError !== null && displayedError !== '' ? (
        <p className="text-sm text-danger">{displayedError}</p>
      ) : null}
    </div>
  )
}
